import { Chart } from 'chart.js/auto'
import { showDistanceLength, showSmallLength, showSmallMoment, showSmallPointLoad } from 'ws-units'

const DATASET_FILTER_TOLERANCE = 1e-9

let unitSystem = {
  smallLength: 'inches',
  smallForce: 'lbs',
  smallMoment: 'lb-in'
}

function getLoadCaseSet(project, loadCaseLabel) {
  switch(loadCaseLabel) {
    case "load_case_results":
        return project.design.loadCases
    case "combination_results":
        return project.design.loadCombination
    case "service_case_results":
        return project.design.serviceCases
    default:
        return null
  }
}

/**
 * Safely gets the equation label for a result case.
 * @param {Object} selectedLoadCase 
 * @param {number} eqIndex 
 * @param {Object} resultCase 
 * @returns {string}
 */
function getEquationLabel(selectedLoadCase, eqIndex, resultCase) {
  if (
    selectedLoadCase.prettyEquations &&
    selectedLoadCase.prettyEquations[eqIndex] &&
    selectedLoadCase.prettyEquations[eqIndex]['EQ']
  ) {
    return selectedLoadCase.prettyEquations[eqIndex]['EQ'];
  }
  return resultCase.equation_name || `EQ${eqIndex + 1}`;
}

/**
 * Filters datasets by a given tolerance.
 * If all y values in a dataset are within the tolerance, the dataset is removed.
 * @param {Array} datasets - Array of datasets to filter.
 * @returns {Array} - Filtered array of datasets.
 */
function filterDatasetsByTolerance(datasets) {
  const tolerance = DATASET_FILTER_TOLERANCE
  datasets = datasets.map( dataset => {
    let p = dataset.data.find( point => Math.abs(point.y) > tolerance )
    if( p == null ) {
      dataset.data = dataset.data.filter( point => Math.abs(point.y) > tolerance )
    }
    return dataset
  })
  return datasets.filter( dataset => dataset.data.length > 0 )  
}

/**
 * Renders displacement, shear, and moment charts for a beam project.
 *
 * @param {Object} project - The BeamProject instance.
 * @param {Object} results - Dictionary of results (from Python API or calculation).
 * @param {boolean} forPrint - Whether the charts are being rendered for print.
 * @param {string} params.loadCaseLabel - The results key to use ("load_case_results", "combination_results", or "service_case_results").
 * @param {string} params.containerId - The HTML element ID for the container to render the charts into.
 * @param {string} params.dispCanvasId - The HTML element ID for the displacement chart canvas.
 * @param {string} params.shearCanvasId - The HTML element ID for the shear chart canvas.
 * @param {string} params.momentCanvasId - The HTML element ID for the moment chart canvas.
 */
function renderResultCharts({ 
  project, 
  results, 
  forPrint, 
  loadCaseLabel, 
  containerId, 
  dispCanvasId, 
  shearCanvasId, 
  momentCanvasId 
}) {
  if (results == null || Object.keys(results).length === 0) return;

  let selectedLoadCase = getLoadCaseSet(project, loadCaseLabel)
  let selectedResults = results[loadCaseLabel]

  let str = String.raw`
    <div class="section-header">Dy (${unitSystem.smallLength})</div>
    <canvas id="${dispCanvasId}" height="250" style="width:100%;height:250px;max-width:100%;"></canvas>
    <div class="section-header">Shear (${unitSystem.smallForce})</div>
    <canvas id="${shearCanvasId}" height="250" style="width:100%;height:250px;max-width:100%;"></canvas>
    <div class="section-header">Moment (${unitSystem.smallMoment})</div>
    <canvas id="${momentCanvasId}" height="250" style="width:100%;height:250px;max-width:100%;"></canvas>`

  document.getElementById(containerId).innerHTML = str

  // Displacements
  let datasets = selectedResults.map(resultCase => {
    let eqIndex = Number(resultCase.equation_name.split('EQ')[1]) - 1
    let equationLabel = getEquationLabel(selectedLoadCase, eqIndex, resultCase)
    const results = resultCase.displacements.map(result => ({
      x: showDistanceLength(Number(result.x)),
      y: showSmallLength(Number(result.dy))
    }))
    return { label: equationLabel, data: results, showLine: true, tension: 0.0, pointRadius: 2, borderWidth: 2 }
  })
  datasets = filterDatasetsByTolerance(datasets)
  let ctx = document.getElementById(dispCanvasId).getContext('2d')
  makeChart(ctx, datasets, showDistanceLength(project.beam.TotalLength()), false)

  // Shear
  datasets = selectedResults.map(resultCase => {
    let eqIndex = Number(resultCase.equation_name.split('EQ')[1]) - 1
    let equationLabel = getEquationLabel(selectedLoadCase, eqIndex, resultCase)
    const results = resultCase.nodal_forces.map(result => ({
      x: showDistanceLength(Number(result.x)),
      y: showSmallPointLoad(Number(result.fy))
    }))
    return { label: equationLabel, data: results, showLine: true, tension: 0.0, pointRadius: 2, borderWidth: 2 }
  })
  datasets = filterDatasetsByTolerance(datasets)
  ctx = document.getElementById(shearCanvasId).getContext('2d')
  makeChart(ctx, datasets, showDistanceLength(project.beam.TotalLength()), false)

  // Moment
  datasets = selectedResults.map(resultCase => {
    let eqIndex = Number(resultCase.equation_name.split('EQ')[1]) - 1
    let equationLabel = getEquationLabel(selectedLoadCase, eqIndex, resultCase)
    const results = resultCase.nodal_forces.map(result => ({
      x: showDistanceLength(Number(result.x)),
      y: showSmallMoment(Number(result.mz))
    }))
    return { label: equationLabel, data: results, showLine: true, tension: 0.0, pointRadius: 2, borderWidth: 2 }
  })
  datasets = filterDatasetsByTolerance(datasets)
  ctx = document.getElementById(momentCanvasId).getContext('2d')
  makeChart(ctx, datasets, showDistanceLength(project.beam.TotalLength()), true)
}

export function showResultPlots(project, results, forPrint) {
  showLoadCasePlots(project, results, forPrint)
  if(project.design.designType !== "None") {
    showLoadCombinationPlots(project, results, forPrint)
  } 
  showServiceCasePlots(project, results, forPrint)
}

function showLoadCasePlots(project, results, forPrint) {
  renderResultCharts({
    project,
    results,
    forPrint,
    loadCaseLabel: "load_case_results",
    containerId: "load-case-plots",
    dispCanvasId: "load-case-beam-disp-graph",
    shearCanvasId: "load-case-beam-shear-graph",
    momentCanvasId: "load-case-beam-moment-graph"
  });
}

function showLoadCombinationPlots(project, results, forPrint) {
  renderResultCharts({
    project,
    results,
    forPrint,
    loadCaseLabel: "combination_results",
    containerId: "load-combination-plots",
    dispCanvasId: "load-combination-beam-disp-graph",
    shearCanvasId: "load-combination-beam-shear-graph",
    momentCanvasId: "load-combination-beam-moment-graph"
  });
}

function showServiceCasePlots(project, results, forPrint) {
  renderResultCharts({
    project,
    results,
    forPrint,
    loadCaseLabel: "service_case_results",
    containerId: "service-case-plots",
    dispCanvasId: "service-case-beam-disp-graph",
    shearCanvasId: "service-case-beam-shear-graph",
    momentCanvasId: "service-case-beam-moment-graph"
  });
}

function makeChart(canvasContext, datasets, maxLength, showLegend) {
  return new Chart(canvasContext, {
    type: 'scatter',
    data: { datasets: datasets },
    options: {
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          ticks: {
            beginAtZero: true,
            max: 100
          },
          min: 0,
          max: maxLength
        },
      },
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom'
        }
      }
    },
  })
}